import {createSelector} from "@reduxjs/toolkit";

import {
    DOC_PROPERTY_SWOT_NEGATIVES,
    DOC_PROPERTY_SWOT_POSITIVES,
    hasEnergyCertificateData,
    hasUserEditedSwot,
    type SwotItem,
    type SwotModel,
    withEnergyEfficiencySwotNegative,
    withEnergyEfficiencySwotPositive,
    withPhotovoltaicSwotPositive,
    withSolarThermalSwotPositive,
} from "@reva/shared";

import {selectEnergyIndicationData} from "./energySelectors";
import {selectActiveValueForSingleParameter} from "./objectParametersSelectors";
import {
    selectAllDocProperties,
    selectAllEnergyParams,
    selectAllIdpEnergyCertParams,
    selectAllParams,
} from "./objectParametersSlice";
import {selectLastValidPersistenceResponse} from "./persistenceSlice";

const EMPTY_MODEL: SwotModel = {items: []};

function docPropertyModel(docProperties: unknown, property: string): SwotModel {
    const value = (docProperties as Record<string, SwotModel | null> | null | undefined)?.[property];
    return value ?? EMPTY_MODEL;
}

/**
 * True, sobald eine der beiden SWOT-Listen als Document Property gespeichert ist — dann hat der Nutzer
 * die SWOT-Analyse gepflegt und keine der Listen wird noch automatisch ergänzt. Ohne gespeicherten Wert
 * stammen die angezeigten Einträge ausschließlich aus dem BesiConverter (die Vorbefüllung in
 * objectParametersSlice wird nicht von sich aus persistiert, sondern erst mit der ersten Nutzer-Eingabe).
 */
const selectSwotEditedByUser = createSelector([selectLastValidPersistenceResponse], (persistenceResponse): boolean =>
    hasUserEditedSwot(persistenceResponse?.documentProperties as Record<string, unknown> | null),
);

/**
 * Effizienzklasse für die SWOT-Standardsätze: der Wert aus dem Energiewert-Output, aber nur wenn
 * überhaupt Energieausweis-Daten vorliegen (sonst ist die Klasse bloß die AVM-Schätzung, #18102).
 */
const selectEnergyEfficiencyForSwot = createSelector(
    [selectEnergyIndicationData, selectAllEnergyParams, selectAllIdpEnergyCertParams, selectAllParams],
    (energyIndicationData, energyParams, idpEnergyParams, avmParams): unknown =>
        // Die Parameter-Maps kommen aus dem noch untypisierten objectParametersSlice (JS) als `object`.
        hasEnergyCertificateData(
            energyParams as Record<string, unknown> | null,
            idpEnergyParams as Record<string, unknown> | null,
            avmParams as Record<string, unknown> | null,
        )
            ? energyIndicationData?.energy_efficiency
            : null,
);

/**
 * Baut die anzuzeigende SWOT-Liste: eine vom Nutzer gepflegte SWOT-Analyse bleibt unverändert, sonst
 * wird die (rein aus dem BesiConverter stammende) Liste um die Standardsätze aus `additions` ergänzt
 * (#18102).
 */
function buildSwotModel(
    docProperties: unknown,
    editedByUser: boolean,
    property: string,
    additions: ReadonlyArray<(items: SwotItem[]) => SwotItem[]>,
): SwotModel {
    const model = docPropertyModel(docProperties, property);
    if (editedByUser) return model;
    const items = additions.reduce((current, add) => add(current), model.items);
    return items === model.items ? model : {items};
}

/**
 * SWOT-Positiv-Liste, wie sie angezeigt und (bei der nächsten Nutzer-Eingabe) persistiert wird —
 * inklusive Standardsätzen für die Effizienzklassen A+ bis C sowie für Photovoltaik-/Solarthermieanlage.
 * Die beiden Checkbox-Parameter werden wie jeder ENERGY_CERT-Parameter aufgelöst — eine manuelle Eingabe
 * hat Vorrang, sonst greift der automatisch ausgelesene idpEnergyCertificate-Wert
 * (resolveActiveParameterValue's ENERGY_CERT-Fallback).
 */
export const selectSwotPositives = createSelector(
    [
        selectAllDocProperties,
        selectSwotEditedByUser,
        selectEnergyEfficiencyForSwot,
        (state) =>
            selectActiveValueForSingleParameter(state, "energy_photovoltaic_present", "ENERGY_CERT")
                ?.activeParameterValue,
        (state) =>
            selectActiveValueForSingleParameter(state, "energy_solar_thermal_present", "ENERGY_CERT")
                ?.activeParameterValue,
    ],
    (docProperties, editedByUser, energyEfficiency, photovoltaicPresent, solarThermalPresent): SwotModel =>
        buildSwotModel(docProperties, editedByUser, DOC_PROPERTY_SWOT_POSITIVES, [
            (items) => withEnergyEfficiencySwotPositive(items, energyEfficiency),
            (items) => withPhotovoltaicSwotPositive(items, photovoltaicPresent),
            (items) => withSolarThermalSwotPositive(items, solarThermalPresent),
        ]),
);

/**
 * SWOT-Negativ-Liste, wie sie angezeigt und (bei der nächsten Nutzer-Eingabe) persistiert wird —
 * inklusive Standardsatz für die Effizienzklassen F bis H.
 */
export const selectSwotNegatives = createSelector(
    [selectAllDocProperties, selectSwotEditedByUser, selectEnergyEfficiencyForSwot],
    (docProperties, editedByUser, energyEfficiency): SwotModel =>
        buildSwotModel(docProperties, editedByUser, DOC_PROPERTY_SWOT_NEGATIVES, [
            (items) => withEnergyEfficiencySwotNegative(items, energyEfficiency),
        ]),
);

/**
 * Selektor für die anzuzeigende SWOT-Liste einer der beiden Document Properties.
 * @param parameter `inspection_swot_positives` oder `inspection_swot_negatives`
 */
export const selectSwotModel = (parameter: string) =>
    parameter === DOC_PROPERTY_SWOT_POSITIVES ? selectSwotPositives : selectSwotNegatives;

/**
 * Selektor: enthält die angezeigte Liste gerade einen automatisch ergänzten Standardsatz, der so noch
 * nicht gespeichert ist? Genutzt, um beim Speichern der einen Liste die andere mit einzufrieren
 * (#18102) — sonst verschwände deren Standardsatz, sobald die Automatik für beide Listen endet.
 * @param parameter `inspection_swot_positives` oder `inspection_swot_negatives`
 */
export const selectSwotHasStandardSentence =
    (parameter: string) =>
    (state: unknown): boolean => {
        const docProperties = selectAllDocProperties(state as never);
        return selectSwotModel(parameter)(state as never) !== docPropertyModel(docProperties, parameter);
    };
